import { useState, useEffect } from "react";
import { C, S, font, fontCond, RULES_SECTIONS, FIXED_HOLIDAYS } from "../constants";
import { Avatar, DeadlineChip } from "../components/ui";
import { RoundTimePicker, getCourtsForDay } from "../lib/courtAvailability";
import { fmtDate, isChallengerBlocked, getBlockStatus, isExpired } from "../lib/helpers";
import { dbUpdateChallenge, dbInsertNotifications, dbSavePlayers, dbUploadPhoto, supabase } from "../lib/supabase";

/** Retorna lista de { date, hour, maxCourts, games[] } com slots ocupados por desafios */
function getOccupiedSlots(challenges, holidays = []) {
  const map = {};
  for (const c of challenges) {
    if (!["agendado", "aguardando_confirmacao"].includes(c.status)) continue;
    const dates = [];
    if (c.confirmedDate) dates.push({ dt: c.confirmedDate, confirmed: true });
    if (c.status === "aguardando_confirmacao" && c.dates) {
      c.dates.filter(d => d).forEach(d => dates.push({ dt: d, confirmed: false }));
    }
    for (const { dt, confirmed } of dates) {
      if (!dt) continue;
      const d = new Date(dt);
      if (isNaN(d)) continue;
      const date = d.toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" });
      const hour = d.toLocaleTimeString("pt-BR", { timeZone: "America/Sao_Paulo", hour: "2-digit", minute: "2-digit", hour12: false });
      const key = date + "|" + hour;
      if (!map[key]) {
        const slots = getCourtsForDay(date, holidays);
        const slot = slots.find(([h]) => h === hour);
        const maxCourts = slot ? slot[1] : 1;
        map[key] = { date, hour, maxCourts, games: [] };
      }
      map[key].games.push({ challenger: c.challengerName, challenged: c.challengedName, confirmed });
    }
  }
  return Object.values(map).sort((a, b) => (a.date + a.hour) < (b.date + b.hour) ? -1 : 1);
}

// ─── Componente de migração de fotos (temporário — remover após uso) ──────────
function MigratePhotosCard({ isMobile, state, gender, migrateLog, setMigrateLog, migrating, setMigrating, migrateDone, setMigrateDone }) {
  async function runMigration() {
    setMigrating(true);
    setMigrateLog(["🔄 Iniciando migração..."]);
    const tables = [
      { name: "players",   gdr: "M" },
      { name: "players_f", gdr: "F" },
    ];
    let total = 0, ok = 0, fail = 0;
    for (const { name: tbl, gdr } of tables) {
      let rows = [];
      if (gdr === gender) {
        rows = state.players.filter(p => p.photo && p.photo.startsWith("data:"));
      } else {
        const { data } = await supabase.from(tbl).select("id, name, photo").not("photo", "is", null);
        rows = (data || []).filter(p => p.photo && p.photo.startsWith("data:"));
      }
      if (rows.length === 0) {
        setMigrateLog(l => [...l, `✓ ${tbl}: nenhuma foto base64 encontrada`]);
        continue;
      }
      setMigrateLog(l => [...l, `📋 ${tbl}: ${rows.length} foto(s) para migrar`]);
      for (const p of rows) {
        total++;
        try {
          const url = await dbUploadPhoto(p.id, p.photo, tbl);
          const { error } = await supabase.from(tbl).update({ photo: url }).eq("id", p.id);
          if (error) throw error;
          ok++;
          setMigrateLog(l => [...l, `  ✓ ${p.name}`]);
        } catch (e) {
          fail++;
          setMigrateLog(l => [...l, `  ✗ ${p.name}: ${e.message}`]);
        }
      }
    }
    setMigrateLog(l => [...l, ``, `✅ Concluído: ${ok} migrados, ${fail} falhas de ${total} total.`, `Você pode remover este card do AdminPanel.`]);
    setMigrating(false);
    setMigrateDone(true);
  }

  return (
    <div style={{ ...(isMobile ? S.cardMobile : S.card), gridColumn: isMobile ? "auto" : "1 / -1", border: "2px solid #f39c12", background: "#fffbf0" }}>
      <div style={{ ...S.title, color: "#b7791f" }}>🖼️ Migrar Fotos para Storage</div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 12 }}>
        Migra todas as fotos base64 do banco para o Supabase Storage, reduzindo o egress em ~80%.
        Execute apenas uma vez. Este card some automaticamente quando não há mais fotos base64.
      </div>
      {migrateLog.length > 0 && (
        <div style={{ background: "#1a1a1a", color: "#00ff88", fontFamily: "monospace", fontSize: 12, borderRadius: 8, padding: 12, marginBottom: 12, maxHeight: 200, overflowY: "auto" }}>
          {migrateLog.map((line, i) => <div key={i}>{line || " "}</div>)}
        </div>
      )}
      {!migrateDone && (
        <button
          style={{ ...S.btn("#f39c12"), width: "100%", opacity: migrating ? 0.6 : 1 }}
          onClick={runMigration}
          disabled={migrating}
        >
          {migrating ? "Migrando... aguarde" : "▶ Iniciar Migração"}
        </button>
      )}
    </div>
  );
}

export default function AdminPanel({
  isMobile, state, sorted, gender, allChallenges,
  // admin tab
  adminTab, setAdminTab,
  // challenge management
  adminChrID, setAdminChrID, adminChdID, setAdminChdID,
  adminChalDate, setAdminChalDate, adminProposeChallenge,
  // propose as player
  propAsChrID, setPropAsChrID, propAsChdID, setPropAsChdID,
  propAsDates, setPropAsDates, adminProposeAsPlayer,
  cancelChalId, setCancelChalId, cancelChalConfirm, setCancelChalConfirm, cancelChallenge,
  editChalId, setEditChalId, editChalDate, setEditChalDate, editChallengeDate,
  mediationSchedule, setMediationSchedule, mediationDate, setMediationDate,
  // result management
  resultChalId, setResultChalId, resultWinner, setResultWinner,
  resultType, setResultType, resultSets, setResultSets,
  resultMatchDate, setResultMatchDate,
  requestResultConfirm, registerResult, resultConfirmModal, setResultConfirmModal,
  editResultId, setEditResultId, editResultWinner, setEditResultWinner,
  editResultType, setEditResultType, editResultSets, setEditResultSets, saveEditResult, deleteResult,
  // player management
  newPlayerName, setNewPlayerName, addPlayer,
  removePlayerId, setRemovePlayerId, removeConfirm, setRemoveConfirm, removePlayer,
  resetPlayerId, setResetPlayerId, resetNewPass, setResetNewPass, resetPassword,
  adjustPlayerId, setAdjustPlayerId, adjustPosition, setAdjustPosition, adjustPlayerPosition,
  editPlayerModal, setEditPlayerModal,
  editPlayerName, setEditPlayerName,
  editPlayerEmail, setEditPlayerEmail,
  editPlayerPhone, setEditPlayerPhone, saveEditPlayer,
  // blocks / inactivity
  inactivePlayer, setInactivePlayer, inactiveSearch, setInactiveSearch,
  inactiveSearchOpen, setInactiveSearchOpen, inactiveDays, setInactiveDays,
  setInactivity, removeInactivity, unblockPlayer,
  inactivityHistory, loadInactivityHistory, histLoading,
  histPlayerSearch, setHistPlayerSearch, histPlayerSearchOpen, setHistPlayerSearchOpen,
  histPlayerId, setHistPlayerId,
  histSince, setHistSince, histUntil, setHistUntil, histPenalty, setHistPenalty,
  addHistoricalInactivity,
  editingHistId, setEditingHistId,
  editHistSince, setEditHistSince, editHistUntil, setEditHistUntil,
  editHistPenalty, setEditHistPenalty,
  updateHistoricalInactivity, deleteHistoricalInactivity, migrateInactivityHistory,
  // blocked dates
  blockDate, setBlockDate, blockAllDay, setBlockAllDay,
  blockHours, setBlockHours, addBlockedDate, removeBlockedDate,
  // holidays
  holidayDate, setHolidayDate, holidayName, setHolidayName,
  addHoliday, removeHoliday,
  // court slots
  slotDayType, setSlotDayType, slotHour, setSlotHour, slotMaxCourts, setSlotMaxCourts,
  addCourtSlot, updateCourtSlot, removeCourtSlot,
  // blocked periods
  periodDateFrom, setPeriodDateFrom, periodDateTo, setPeriodDateTo,
  periodAllDay, setPeriodAllDay, periodHours, setPeriodHours,
  periodLabel, setPeriodLabel,
  addBlockedPeriod, removeBlockedPeriod,
  // rules
  editingRule, setEditingRule,
  editRuleIcon, setEditRuleIcon,
  editRuleTitle, setEditRuleTitle,
  editRuleText, setEditRuleText, saveRule,
  // flash
  flash, save,
}) {
  const selChal = state.challenges.find(c => c.id === parseInt(resultChalId));
  const selChr  = selChal ? state.players.find(p => p.id === selChal.challengerId) : null;
  const selChd  = selChal ? state.players.find(p => p.id === selChal.challengedId) : null;

  const [editingInactiveId, setEditingInactiveId] = useState(null);
  const [editInactiveUntil, setEditInactiveUntil] = useState("");
  const [confirmEndId, setConfirmEndId] = useState(null);
  const [unblockConfirm, setUnblockConfirm] = useState(null); // { pid, type }
  const [deleteResultConfirm, setDeleteResultConfirm] = useState(false);
  const [editingSlotId, setEditingSlotId] = useState(null);
  const [editSlotCourts, setEditSlotCourts] = useState(1);

  // ── Migração de fotos ──────────────────────────────────────────────────────
  const [migrateLog, setMigrateLog]   = useState([]);
  const [migrating, setMigrating]     = useState(false);
  const [migrateDone, setMigrateDone] = useState(false);

  const nowBlocks = new Date();
  const currentlyInactive = sorted.filter(p => p.inactiveUntil && new Date(p.inactiveUntil) > nowBlocks);

  useEffect(() => {
    if (adminTab === "inactivity" && inactivityHistory.length === 0 && !histLoading) {
      loadInactivityHistory();
    }
  }, [adminTab]);
  const penaltyChip = (ytd) => {
    if (ytd <= 30)  return { label: "Faixa I - Sem penalidade",  bg: "#eaf6ee", color: C.success };
    if (ytd <= 60)  return { label: "Faixa II - 5 posicoes",     bg: "#fff8e1", color: "#b7791f" };
    if (ytd <= 90)  return { label: "Faixa III - 10 posicoes",   bg: "#fff3e0", color: "#e67e22" };
    if (ytd <= 120) return { label: "Faixa IV - 15 posicoes",    bg: "#fdecea", color: C.primary };
    if (ytd <= 150) return { label: "Faixa V - 20 posicoes",     bg: "#fdecea", color: C.primary };
    return                 { label: "Faixa VI - 25 posicoes",    bg: "#f8d7da", color: "#c0392b" };
  };

  const fmt = d => d ? new Date(d).toLocaleDateString("pt-BR") : "—";

  const activeInactives = sorted.filter(p => p.inactiveUntil && new Date(p.inactiveUntil) > new Date());
  const blockedByResult = sorted.filter(p => {
    const hasBlockChr = p.blockedAsChallenger && new Date(p.blockedAsChallenger) > new Date();
    const hasBlockChd = p.blockedAsChallenged && new Date(p.blockedAsChallenged) > new Date();
    return hasBlockChr || hasBlockChd;
  });

  async function saveInactiveUntil(p) {
    if (!editInactiveUntil) { flash("Selecione uma data.", "error"); return; }
    const s = JSON.parse(JSON.stringify(state));
    const player = s.players.find(x => x.id === p.id);
    if (!player) return;
    player.inactiveUntil = new Date(editInactiveUntil + "T23:59:59-03:00").toISOString();
    try {
      await dbSavePlayers([player]);
      save(s);
      setEditingInactiveId(null);
      flash("Inatividade atualizada para " + player.name + ".");
    } catch (e) { flash("Erro ao atualizar.", "error"); }
  }

  // Build unified holiday list: fixed nationals + manual from DB
  const manualHolidays = (state.holidays || []);
  const fixedHolidaysList = FIXED_HOLIDAYS.map(h => {
    const year = new Date().getFullYear();
    const date = `${year}-${String(h.month).padStart(2,"0")}-${String(h.day).padStart(2,"0")}`;
    return { date, name: h.name, isFixed: true };
  });
  const allHolidaysSorted = [
    ...fixedHolidaysList,
    ...manualHolidays.map(h => ({ ...h, isFixed: false })),
  ].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div>
      {/* ── Sub-tab nav ─────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "center", borderBottom: "1px solid " + C.border, marginBottom: 16, overflowX: "auto" }}>
        {[["challenges","⚔️ Desafios"],["results","🏆 Resultados"],["players","👥 Jogadores"],["blocks","🔒 Bloqueios"],["courts","🎾 Quadras"],["inactivity","😴 Inatividade"],["rules","📖 Regras"]].map(([k,l]) => (
          <button key={k} style={{ ...S.adminTab(adminTab === k), flexShrink: 0, minHeight: 44 }} onClick={() => setAdminTab(k)}>{l}</button>
        ))}
      </div>

      {/* ── Challenges ─────────────────────────────────────────────────────── */}
      {adminTab === "challenges" && (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>

          {/* Mediation — full width, top */}
          {state.challenges.filter(c => c.status === "em_mediacao").length > 0 && (
            <div style={{ ...(isMobile ? S.cardMobile : S.card), gridColumn: isMobile ? "auto" : "1 / -1", border: "1px solid #8e44ad55", background: "#fdf5ff" }}>
              <div style={{ ...S.title, color: "#8e44ad" }}>🛡️ Desafios em Mediação</div>
              {state.challenges.filter(c => c.status === "em_mediacao").map(c => {
                const chr = state.players.find(p => p.id === c.challengerId);
                const chd = state.players.find(p => p.id === c.challengedId);
                return (
                  <div key={c.id} style={{ padding: "12px 0", borderBottom: "1px solid " + C.border }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>{chr?chr.name:"-"} vs {chd?chd.name:"-"}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
                      Datas propostas: {c.dates.filter(d=>d).map(d=>fmtDate(d)).join(" • ") || "—"}
                    </div>
                    {mediationSchedule?.challengeId === c.id ? (
                      <div style={{ width: "100%" }}>
                        <RoundTimePicker
                          value={mediationDate} onChange={v => setMediationDate(v)}
                          style={{ ...S.input, marginBottom: 8 }}
                          allChallenges={allChallenges.filter(x => x.id !== c.id)}
                          otherDates={[]}
                          blockedDates={state.blockedDates}
                          holidays={state.holidays || []}
                          courtSlots={state.courtSlots || []}
                          blockedPeriods={state.blockedPeriods || []}
                        />
                        <div style={{ display: "flex", gap: 8 }}>
                          <button style={{ ...S.btn(C.success), flex: 1, fontSize: 12 }} onClick={async () => {
                            if (!mediationDate) { flash("Selecione uma data.", "error"); return; }
                            const s = JSON.parse(JSON.stringify(state));
                            const ch = s.challenges.find(x => x.id === c.id);
                            const chr2 = s.players.find(p => p.id === c.challengerId);
                            const chd2 = s.players.find(p => p.id === c.challengedId);
                            if (!ch) return;
                            ch.status = "agendado"; ch.confirmedDate = mediationDate;
                            const now = new Date().toISOString();
                            try {
                              await dbUpdateChallenge(c.id, { status: "agendado", confirmedDate: mediationDate }, gender);
                              const notifs = [
                                { playerId: c.challengerId, text: `Admin agendou seu desafio vs ${chd2?.name||""} para ${fmtDate(mediationDate)}.`, date: now, read: false },
                                { playerId: c.challengedId, text: `Admin agendou seu desafio vs ${chr2?.name||""} para ${fmtDate(mediationDate)}.`, date: now, read: false },
                              ];
                              await dbInsertNotifications(notifs, gender);
                              notifs.forEach(n => s.notifications.unshift({ ...n, id: Date.now() + Math.random() }));
                              save(s); setMediationSchedule(null); setMediationDate("");
                              flash("Desafio agendado pelo admin.");
                            } catch(e) { console.error(e); flash("Erro ao agendar.", "error"); }
                          }}>Confirmar</button>
                          <button style={{ ...S.btn(C.muted, true), flex: 1, fontSize: 12 }} onClick={() => { setMediationSchedule(null); setMediationDate(""); }}>Cancelar</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button style={{ ...S.btn(C.success), fontSize: 12, padding: "6px 12px" }} onClick={() => { setMediationSchedule({ challengeId: c.id }); setMediationDate(""); }}>Agendar</button>
                        <button style={{ ...S.btn(C.primary), fontSize: 12, padding: "6px 12px" }} onClick={() => { setCancelChalId(String(c.id)); setCancelChalConfirm(false); }}>Cancelar desafio</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Schedule challenge */}
          <div style={isMobile ? S.cardMobile : S.card}>
            <div style={S.title}>📅 Agendar Desafio</div>
            <label style={S.label}>Desafiante</label>
            <select style={S.select} value={adminChrID} onChange={e => setAdminChrID(e.target.value)}>
              <option value="">Selecione</option>
              {sorted.map(p => <option key={p.id} value={p.id}>{p.position}. {p.name}</option>)}
            </select>
            <label style={S.label}>Desafiado</label>
            <select style={S.select} value={adminChdID} onChange={e => setAdminChdID(e.target.value)}>
              <option value="">Selecione</option>
              {sorted.filter(p => p.id !== parseInt(adminChrID)).map(p => <option key={p.id} value={p.id}>{p.position}. {p.name}</option>)}
            </select>
            <label style={S.label}>Data</label>
            <RoundTimePicker value={adminChalDate} onChange={v => setAdminChalDate(v)} style={{ ...S.input }} allChallenges={allChallenges} blockedDates={state.blockedDates} holidays={state.holidays || []} courtSlots={state.courtSlots || []} blockedPeriods={state.blockedPeriods || []} />
            <button style={{ ...S.btn(), width: "100%", marginTop: 8 }} onClick={adminProposeChallenge}>Agendar</button>
          </div>

          {/* Propose as player */}
          <div style={isMobile ? S.cardMobile : S.card}>
            <div style={S.title}>🎾 Propor como Jogador</div>
            <label style={S.label}>Desafiante</label>
            <select style={S.select} value={propAsChrID} onChange={e => setPropAsChrID(e.target.value)}>
              <option value="">Selecione</option>
              {sorted.map(p => <option key={p.id} value={p.id}>{p.position}. {p.name}</option>)}
            </select>
            <label style={S.label}>Desafiado</label>
            <select style={S.select} value={propAsChdID} onChange={e => setPropAsChdID(e.target.value)}>
              <option value="">Selecione</option>
              {sorted.filter(p => p.id !== parseInt(propAsChrID)).map(p => <option key={p.id} value={p.id}>{p.position}. {p.name}</option>)}
            </select>
            <label style={S.label}>3 opções de data</label>
            {propAsDates.map((d, i) => (
              <RoundTimePicker
                key={i}
                value={d}
                onChange={v => { const nd = [...propAsDates]; nd[i] = v; setPropAsDates(nd); }}
                style={{ ...S.input, marginBottom: 6 }}
                allChallenges={allChallenges}
                otherDates={propAsDates.filter((_, j) => j !== i)}
                blockedDates={state.blockedDates}
                holidays={state.holidays || []}
                courtSlots={state.courtSlots || []}
                blockedPeriods={state.blockedPeriods || []}
              />
            ))}
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>
              Desafio ficará pendente até o desafiado confirmar uma das datas.
            </div>
            <button style={{ ...S.btn(C.accent), width: "100%", marginTop: 4 }} onClick={adminProposeAsPlayer}>Propor Desafio</button>
          </div>

          {/* Cancel challenge */}
          <div style={isMobile ? S.cardMobile : S.card}>
            <div style={S.title}>✗ Cancelar Desafio</div>
            <select style={S.select} value={cancelChalId} onChange={e => { setCancelChalId(e.target.value); setCancelChalConfirm(false); }}>
              <option value="">Selecione o desafio</option>
              {[...state.challenges].sort((a,b) => new Date(a.confirmedDate||a.proposedAt) - new Date(b.confirmedDate||b.proposedAt)).map(c => {
                const a = state.players.find(p => p.id === c.challengerId);
                const b = state.players.find(p => p.id === c.challengedId);
                return <option key={c.id} value={c.id}>#{c.id} — {a?a.name:"-"} vs {b?b.name:"-"}</option>;
              })}
            </select>
            {cancelChalId && (
              <div style={S.danger}>
                <div style={{ fontWeight: 700, color: C.primary, marginBottom: 6 }}>⚠️ Confirmar cancelamento?</div>
                <div style={{ fontSize: 13, color: C.muted, marginBottom: 10 }}>
                  {(() => {
                    const ch = state.challenges.find(c => c.id === parseInt(cancelChalId));
                    const a = ch && state.players.find(p => p.id === ch.challengerId);
                    const b = ch && state.players.find(p => p.id === ch.challengedId);
                    return a && b ? `${a.name} vs ${b.name}` : "";
                  })()}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button style={{ ...S.btn(C.primary), flex: 1 }} onClick={cancelChallenge}>Cancelar desafio</button>
                  <button style={{ ...S.btn(C.muted, true), flex: 1 }} onClick={() => setCancelChalId("")}>Voltar</button>
                </div>
              </div>
            )}
          </div>

          {/* Edit challenge date — half width, alongside cancel */}
          <div style={isMobile ? S.cardMobile : S.card}>
            <div style={S.title}>✏️ Alterar Data de Desafio</div>
            <label style={S.label}>Desafio</label>
            <select style={S.select} value={editChalId} onChange={e => {
              setEditChalId(e.target.value);
              const ch = state.challenges.find(c => c.id === parseInt(e.target.value));
              setEditChalDate(ch?.confirmedDate || "");
            }}>
              <option value="">Selecione</option>
              {[...state.challenges].sort((a,b) => new Date(a.confirmedDate||a.proposedAt) - new Date(b.confirmedDate||b.proposedAt)).map(c => {
                const a = state.players.find(p => p.id === c.challengerId);
                const b = state.players.find(p => p.id === c.challengedId);
                return <option key={c.id} value={c.id}>#{c.id} — {a?a.name:"-"} vs {b?b.name:"-"}</option>;
              })}
            </select>
            <label style={S.label}>Nova data</label>
            <RoundTimePicker
              value={editChalDate} onChange={v => setEditChalDate(v)}
              style={{ ...S.input }}
              allChallenges={allChallenges.filter(c => c.id !== parseInt(editChalId))}
              otherDates={[]}
              blockedDates={state.blockedDates}
              holidays={state.holidays || []}
              courtSlots={state.courtSlots || []}
              blockedPeriods={state.blockedPeriods || []}
            />
            <button style={{ ...S.btn(C.accent), marginTop: 8, width: "100%" }} onClick={editChallengeDate}>Salvar Nova Data</button>
          </div>
        </div>
      )}

      {/* ── Results ────────────────────────────────────────────────────────── */}
      {adminTab === "results" && (
        <div>
          {/* Register result */}
          <div style={isMobile ? S.cardMobile : S.card}>
            <div style={S.title}>🏆 Registrar Resultado</div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
              <div>
                <label style={S.label}>Desafio</label>
                <select style={S.select} value={resultChalId} onChange={e => setResultChalId(e.target.value)}>
                  <option value="">Selecione</option>
                  {[...state.challenges].sort((a,b) => new Date(a.confirmedDate||a.proposedAt) - new Date(b.confirmedDate||b.proposedAt)).map(c => {
                    const a = state.players.find(p => p.id === c.challengerId);
                    const b = state.players.find(p => p.id === c.challengedId);
                    return <option key={c.id} value={c.id}>#{c.id} — {a?a.name:"-"} vs {b?b.name:"-"}</option>;
                  })}
                </select>
                <label style={S.label}>Data do jogo</label>
                <input
                  type="date"
                  style={{ ...S.input }}
                  value={resultMatchDate}
                  onChange={e => setResultMatchDate(e.target.value)}
                />
                <label style={S.label}>Tipo</label>
                <select style={S.select} value={resultType} onChange={e => setResultType(e.target.value)}>
                  <option value="normal">Jogo normal</option>
                  <option value="wo-challenged">W.O. — {selChd ? selChd.name : "Desafiado"} não compareceu</option>
                  <option value="wo-challenger">W.O. — {selChr ? selChr.name : "Desafiante"} não compareceu</option>
                </select>
                {resultType === "normal" && (<>
                  <label style={S.label}>Vencedor</label>
                  <select style={S.select} value={resultWinner} onChange={e => setResultWinner(e.target.value)}>
                    <option value="">Selecione</option>
                    <option value="challenger">Desafiante {selChr ? "("+selChr.name+")" : ""}</option>
                    <option value="challenged">Desafiado {selChd ? "("+selChd.name+")" : ""}</option>
                  </select>
                </>)}
              </div>
              {resultType === "normal" && (
                <div>
                  <label style={S.label}>Placar</label>
                  {resultSets.map((set, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                      <span style={{ fontSize: 12, color: C.muted, minWidth: 40, flexShrink: 0 }}>Set {i+1}</span>
                      <input style={{ ...S.input, marginBottom: 0, flex: 1, textAlign: "center" }} type="number" min={0} max={99} value={set.a} onChange={e => { const ns = [...resultSets]; ns[i] = {...ns[i], a: e.target.value}; setResultSets(ns); }}/>
                      <span style={{ fontWeight: 700, color: C.muted }}>×</span>
                      <input style={{ ...S.input, marginBottom: 0, flex: 1, textAlign: "center" }} type="number" min={0} max={99} value={set.b} onChange={e => { const ns = [...resultSets]; ns[i] = {...ns[i], b: e.target.value}; setResultSets(ns); }}/>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button style={{ ...S.btn(C.success), marginTop: 8, width: "100%" }} onClick={requestResultConfirm}>Registrar Resultado</button>
          </div>

          {/* Edit existing result */}
          <div style={isMobile ? S.cardMobile : S.card}>
            <div style={S.title}>✏️ Editar Resultado Existente</div>
            <label style={S.label}>Selecionar resultado</label>
            <select style={S.select} value={editResultId || ""} onChange={e => {
              const id = parseInt(e.target.value);
              setEditResultId(id || null);
              setDeleteResultConfirm(false);
              if (id) {
                const r = state.results.find(x => x.id === id);
                if (r) {
                  setEditResultType(r.type || "normal");
                  setEditResultWinner(r.winner || "");
                  if (r.score) {
                    const parts = r.score.split(", ").map(s => { const [a,b] = s.split("/"); return {a:a||"",b:b||""}; });
                    setEditResultSets([...parts, ...Array(3-parts.length).fill({a:"",b:""})].slice(0,3));
                  } else {
                    setEditResultSets([{a:"",b:""},{a:"",b:""},{a:"",b:""}]);
                  }
                }
              }
            }}>
              <option value="">Selecione</option>
              {[...state.results].reverse().map(r => (
                <option key={r.id} value={r.id}>
                  {new Date(r.date).toLocaleDateString("pt-BR")} — {r.challengerName} vs {r.challengedName}
                </option>
              ))}
            </select>
            {editResultId && (() => {
              const r = state.results.find(x => x.id === editResultId);
              if (!r) return null;
              return (
                <div>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12, marginTop: 8 }}>
                    <div>
                      <label style={S.label}>Tipo</label>
                      <select style={S.select} value={editResultType} onChange={e => setEditResultType(e.target.value)}>
                        <option value="normal">Jogo normal</option>
                        <option value="wo-challenged">W.O. — {r.challengedName} não compareceu</option>
                        <option value="wo-challenger">W.O. — {r.challengerName} não compareceu</option>
                      </select>
                      {editResultType === "normal" && (<>
                        <label style={S.label}>Vencedor</label>
                        <select style={S.select} value={editResultWinner} onChange={e => setEditResultWinner(e.target.value)}>
                          <option value="">Selecione</option>
                          <option value="challenger">Desafiante ({r.challengerName})</option>
                          <option value="challenged">Desafiado ({r.challengedName})</option>
                        </select>
                      </>)}
                    </div>
                    {editResultType === "normal" && (
                      <div>
                        <label style={S.label}>Placar</label>
                        {editResultSets.map((set, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                            <span style={{ fontSize: 12, color: C.muted, minWidth: 40, flexShrink: 0 }}>Set {i+1}</span>
                            <input style={{ ...S.input, marginBottom: 0, flex: 1, textAlign: "center" }} type="number" min={0} max={99} value={set.a} onChange={e => { const ns = [...editResultSets]; ns[i] = {...ns[i], a: e.target.value}; setEditResultSets(ns); }}/>
                            <span style={{ fontWeight: 700, color: C.muted }}>×</span>
                            <input style={{ ...S.input, marginBottom: 0, flex: 1, textAlign: "center" }} type="number" min={0} max={99} value={set.b} onChange={e => { const ns = [...editResultSets]; ns[i] = {...ns[i], b: e.target.value}; setEditResultSets(ns); }}/>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button style={{ ...S.btn(C.warning), marginTop: 8, width: "100%" }} onClick={saveEditResult}>Salvar Alterações</button>
                  {!deleteResultConfirm ? (
                    <button
                      style={{ ...S.btn(C.primary), marginTop: 8, width: "100%", background: "#fff0f0", color: C.primary, border: "1px solid " + C.primary + "55" }}
                      onClick={() => setDeleteResultConfirm(true)}>
                      🗑️ Apagar Resultado
                    </button>
                  ) : (
                    <div style={{ marginTop: 8, background: "#fff0f0", border: "1px solid " + C.primary + "55", borderRadius: 10, padding: "10px 12px" }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.primary, marginBottom: 8 }}>Confirmar exclusão? Esta ação não pode ser desfeita.</div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button style={{ ...S.btn(C.primary), flex: 1, margin: 0 }} onClick={() => { deleteResult(editResultId); setDeleteResultConfirm(false); }}>Sim, apagar</button>
                        <button style={{ ...S.btn(), flex: 1, margin: 0, background: "#f0f0f0", color: C.muted }} onClick={() => setDeleteResultConfirm(false)}>Cancelar</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── Players ────────────────────────────────────────────────────────── */}
      {adminTab === "players" && (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>

          {/* ── Migrar Fotos para Storage (rodar uma vez) ── */}
          {(state.players.some(p => p.photo && p.photo.startsWith("data:")) || migrateDone || migrateLog.length > 0) && (
            <MigratePhotosCard
              isMobile={isMobile}
              state={state}
              gender={gender}
              migrateLog={migrateLog}
              setMigrateLog={setMigrateLog}
              migrating={migrating}
              setMigrating={setMigrating}
              migrateDone={migrateDone}
              setMigrateDone={setMigrateDone}
            />
          )}

          {/* Add */}
          <div style={isMobile ? S.cardMobile : S.card}>
            <div style={S.title}>➕ Adicionar Jogador</div>
            <input style={S.input} placeholder="Nome completo" value={newPlayerName} onChange={e => setNewPlayerName(e.target.value)} onKeyDown={e => e.key === "Enter" && addPlayer()} autoCapitalize="words"/>
            <button style={{ ...S.btn(), width: "100%" }} onClick={addPlayer}>Adicionar</button>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>Posição final. Senha padrão: senha123</div>
          </div>

          {/* Remove */}
          <div style={isMobile ? S.cardMobile : S.card}>
            <div style={S.title}>🗑️ Remover Jogador</div>
            <select style={S.select} value={removePlayerId} onChange={e => { setRemovePlayerId(e.target.value); setRemoveConfirm(false); }}>
              <option value="">Selecione</option>
              {sorted.map(p => <option key={p.id} value={p.id}>{p.position}. {p.name}</option>)}
            </select>
            {removePlayerId && !removeConfirm && (
              <div style={S.danger}>
                <div style={{ fontWeight: 700, color: C.primary, marginBottom: 8 }}>⚠️ Atenção</div>
                <div style={{ fontSize: 13, color: C.muted, marginBottom: 10 }}>
                  Remover <b style={{ color: C.text }}>{sorted.find(p => p.id === parseInt(removePlayerId))?.name}</b> é irreversível.
                </div>
                <button style={{ ...S.btn(C.primary), width: "100%" }} onClick={() => setRemoveConfirm(true)}>Confirmar Remoção</button>
              </div>
            )}
            {removePlayerId && removeConfirm && (
              <div style={{ display: "flex", gap: 10 }}>
                <button style={{ ...S.btn(C.primary), flex: 1 }} onClick={removePlayer}>Sim, remover</button>
                <button style={{ ...S.btn(C.muted, true), flex: 1 }} onClick={() => setRemoveConfirm(false)}>Cancelar</button>
              </div>
            )}
          </div>

          {/* Edit player */}
          <div style={isMobile ? S.cardMobile : S.card}>
            <div style={S.title}>✏️ Editar Jogador</div>
            <select style={S.select} onChange={e => {
              const p = sorted.find(x => x.id === parseInt(e.target.value));
              if (p) { setEditPlayerModal(p); setEditPlayerName(p.name); setEditPlayerEmail(p.email||""); setEditPlayerPhone(p.phone||""); }
            }} value={editPlayerModal?.id || ""}>
              <option value="">Selecione</option>
              {sorted.map(p => <option key={p.id} value={p.id}>{p.position}. {p.name}</option>)}
            </select>
            {editPlayerModal && (
              <div>
                <label style={S.label}>Nome</label>
                <input style={S.input} value={editPlayerName} onChange={e => setEditPlayerName(e.target.value)} autoCapitalize="words"/>
                <label style={S.label}>Email</label>
                <input style={S.input} type="email" placeholder="email@exemplo.com" value={editPlayerEmail} onChange={e => setEditPlayerEmail(e.target.value)}/>
                <label style={S.label}>Telefone</label>
                <input style={S.input} type="tel" placeholder="(11) 99999-9999" value={editPlayerPhone} onChange={e => setEditPlayerPhone(e.target.value)}/>
                <button style={{ ...S.btn(C.accent), width: "100%" }} onClick={saveEditPlayer}>Salvar</button>
              </div>
            )}
          </div>

          {/* Reset password */}
          <div style={isMobile ? S.cardMobile : S.card}>
            <div style={S.title}>🔑 Redefinir Senha</div>
            <select style={S.select} value={resetPlayerId} onChange={e => setResetPlayerId(e.target.value)}>
              <option value="">Selecione</option>
              {sorted.map(p => <option key={p.id} value={p.id}>{p.position}. {p.name}</option>)}
            </select>
            <input style={S.input} placeholder="Nova senha" value={resetNewPass} onChange={e => setResetNewPass(e.target.value)}/>
            <button style={{ ...S.btn(), width: "100%" }} onClick={resetPassword}>Redefinir</button>
          </div>

          {/* Adjust position */}
          <div style={isMobile ? S.cardMobile : S.card}>
            <div style={S.title}>📍 Ajustar Posição</div>
            <select style={S.select} value={adjustPlayerId} onChange={e => setAdjustPlayerId(e.target.value)}>
              <option value="">Selecione</option>
              {sorted.map(p => <option key={p.id} value={p.id}>{p.position}. {p.name}</option>)}
            </select>
            <input style={S.input} type="number" min={1} max={sorted.length} placeholder={"Nova posição (1–"+sorted.length+")"} value={adjustPosition} onChange={e => setAdjustPosition(e.target.value)}/>
            <button style={{ ...S.btn(), width: "100%" }} onClick={adjustPlayerPosition}>Ajustar</button>
          </div>
        </div>
      )}

      {/* ── Blocks ─────────────────────────────────────────────────────────── */}
      {adminTab === "blocks" && (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>

          {/* Indisponíveis por resultado */}
          <div style={isMobile ? S.cardMobile : S.card}>
            <div style={S.title}>🔓 Indisponíveis por Resultado</div>
            {blockedByResult.length === 0 && (
              <div style={{ color: C.muted, fontSize: 13 }}>Nenhum jogador bloqueado por resultado.</div>
            )}
            {blockedByResult.map(p => {
              const bChr = p.blockedAsChallenger && new Date(p.blockedAsChallenger) > new Date() ? p.blockedAsChallenger : null;
              const bChd = p.blockedAsChallenged && new Date(p.blockedAsChallenged) > new Date() ? p.blockedAsChallenged : null;
              const confirmKey = unblockConfirm?.pid === p.id ? unblockConfirm.type : null;
              return (
                <div key={p.id} style={{ padding: "12px 0", borderBottom: "1px solid " + C.border }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Avatar player={p} size={32}/>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.position}. {p.name}</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 4 }}>
                        {bChr && <span style={{ fontSize: 11, color: C.warning }}>🚫 Indisponível para desafiar até {fmt(bChr)}</span>}
                        {bChd && <span style={{ fontSize: 11, color: C.primary }}>🛡️ Indisponível para receber até {fmt(bChd)}</span>}
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5, flexShrink: 0 }}>
                      {bChr && (
                        <button
                          style={{ ...S.btn(C.warning, confirmKey === "challenger"), padding: "5px 10px", fontSize: 11 }}
                          onClick={() => setUnblockConfirm(confirmKey === "challenger" ? null : { pid: p.id, type: "challenger" })}
                        >🚫 Desafiar</button>
                      )}
                      {bChd && (
                        <button
                          style={{ ...S.btn(C.primary, confirmKey === "challenged"), padding: "5px 10px", fontSize: 11 }}
                          onClick={() => setUnblockConfirm(confirmKey === "challenged" ? null : { pid: p.id, type: "challenged" })}
                        >🛡️ Receber</button>
                      )}
                    </div>
                  </div>
                  {confirmKey && (
                    <div style={{ marginTop: 10, padding: "10px 12px", background: "#fff8f0", borderRadius: 8, border: "1px solid #e67e2244" }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6 }}>
                        Remover bloqueio {confirmKey === "challenger" ? "para desafiar" : "para receber desafios"} de {p.name}?
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button style={{ ...S.btn(C.accent), flex: 1, fontSize: 12 }} onClick={() => { unblockPlayer(p.id, confirmKey); setUnblockConfirm(null); }}>Confirmar</button>
                        <button style={{ ...S.btn(C.muted, true), flex: 1, fontSize: 12 }} onClick={() => setUnblockConfirm(null)}>Cancelar</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Bloquear Datas */}
          <div style={isMobile ? S.cardMobile : S.card}>
            <div style={S.title}>🚫 Bloquear Datas</div>

            <div style={{ marginBottom: 28 }}>
              <label style={S.label}>Data</label>
              <input
                type="date"
                style={{ ...S.input }}
                value={blockDate}
                onChange={e => setBlockDate(e.target.value)}
              />
              <label style={S.label}>Tipo de bloqueio</label>
              <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                <button
                  style={{ ...S.btn(blockAllDay ? C.primary : C.muted, !blockAllDay), flex: 1 }}
                  onClick={() => { setBlockAllDay(true); setBlockHours([]); }}
                >🔒 Dia inteiro</button>
                <button
                  style={{ ...S.btn(!blockAllDay ? C.primary : C.muted, blockAllDay), flex: 1 }}
                  onClick={() => setBlockAllDay(false)}
                >🕐 Por horário</button>
              </div>
              {!blockAllDay && blockDate && (() => {
                const slots = (() => {
                  if (!blockDate) return [];
                  const dow = new Date(blockDate + "T12:00:00").getDay();
                  if (dow === 6) return [14,16,18,20,22];
                  if (dow === 0) { const hs = []; for(let h=14;h<=20;h++) hs.push(h); return hs; }
                  const hs = []; for(let h=6;h<=14;h++) hs.push(h); hs.push(19,21,23); return hs;
                })();
                return (
                  <div>
                    <label style={S.label}>Horários a bloquear</label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {slots.map(h => {
                        const hStr = String(h).padStart(2,"0") + ":00";
                        const sel = blockHours.includes(h);
                        return (
                          <button key={h}
                            style={{ ...S.btn(sel ? C.primary : C.muted, !sel), padding: "6px 10px", fontSize: 13, minWidth: 56 }}
                            onClick={() => setBlockHours(sel ? blockHours.filter(x => x !== h) : [...blockHours, h])}
                          >{hStr}</button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              <button style={{ ...S.btn(C.primary), width: "100%", marginTop: 10 }} onClick={addBlockedDate}>
                Confirmar Bloqueio
              </button>
            </div>

            {/* Bloqueios ativos */}
            <div style={{ marginBottom: 28 }}>
              <label style={S.label}>Bloqueios ativos</label>
              {(state.blockedDates).length === 0 && (
                <div style={{ color: C.muted, fontSize: 13 }}>Nenhuma data bloqueada.</div>
              )}
              {[...(state.blockedDates)].sort((a,b) => a.date.localeCompare(b.date)).map(b => (
                <div key={b.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid " + C.border }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                      {new Date(b.date + "T12:00:00").toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" })}
                    </div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                      {b.allDay ? "🔒 Dia inteiro" : "🕐 " + (b.blockedHours || []).map(h => String(h).padStart(2,"0")+":00").join(", ")}
                    </div>
                  </div>
                  <button
                    style={{ ...S.btn(C.primary), padding: "6px 12px", fontSize: 12, flexShrink: 0 }}
                    onClick={() => removeBlockedDate(b.id)}
                  >✕ Remover</button>
                </div>
              ))}
            </div>

            {/* Horários ocupados por jogos */}
            {(() => {
              const occupied = getOccupiedSlots(state.challenges, state.holidays || []);
              if (!occupied.length) return null;
              return (
                <div>
                  <label style={S.label}>Horários ocupados por jogos</label>
                  {occupied.map(({ date, hour, maxCourts, games }) => {
                    const full = games.length >= maxCourts;
                    const dateLabel = new Date(date + "T12:00:00").toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" });
                    return (
                      <div key={date + hour} style={{ padding: "10px 0", borderBottom: "1px solid " + C.border }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                          <span style={{ fontWeight: 700, fontSize: 13 }}>{dateLabel} — {hour}</span>
                          <span style={{
                            fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 20,
                            background: full ? "#fdecea" : "#eaf6ee",
                            color: full ? C.primary : C.success,
                          }}>
                            {games.length}/{maxCourts} {full ? "🔴 LOTADO" : "🟢 " + (maxCourts - games.length) + " livre"}
                          </span>
                        </div>
                        {games.map((g, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.muted, paddingLeft: 4, marginBottom: 3 }}>
                            <span>🎾</span>
                            <span style={{ color: C.text, fontWeight: 500 }}>{g.challenger}</span>
                            <span>vs</span>
                            <span style={{ color: C.text, fontWeight: 500 }}>{g.challenged}</span>
                            <span style={{
                              fontSize: 10, padding: "1px 6px", borderRadius: 10,
                              background: g.confirmed ? "#eaf6ee" : "#fff8e1",
                              color: g.confirmed ? C.success : "#b7791f",
                              marginLeft: 4,
                            }}>
                              {g.confirmed ? "confirmado" : "pendente"}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* Bloqueios por Período — full width */}
          <div style={{ ...(isMobile ? S.cardMobile : S.card), gridColumn: isMobile ? "auto" : "1 / -1" }}>
            <div style={S.title}>🚫 Bloquear por Período</div>
            {(state.blockedPeriods || []).length === 0 && <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>Nenhum período bloqueado.</div>}
            {(state.blockedPeriods || []).map(p => (
              <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid " + C.border, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>
                    {new Date(p.dateFrom + "T12:00:00").toLocaleDateString("pt-BR")} → {new Date(p.dateTo + "T12:00:00").toLocaleDateString("pt-BR")}
                  </span>
                  {p.label && <span style={{ fontSize: 12, color: C.muted, marginLeft: 8 }}>{p.label}</span>}
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                    {p.allDay ? "🔒 Dia inteiro" : `🕐 Horários: ${(p.blockedHours || []).map(h => String(h).padStart(2,"0") + "h").join(", ")}`}
                  </div>
                </div>
                <button style={{ ...S.btn(C.primary), padding: "6px 12px", fontSize: 12, flexShrink: 0 }} onClick={() => removeBlockedPeriod(p.id)}>✕ Remover</button>
              </div>
            ))}
            <div style={{ marginTop: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
                <div>
                  <label style={S.label}>De</label>
                  <input type="date" style={{ ...S.input, marginBottom: 0 }} value={periodDateFrom} onChange={e => setPeriodDateFrom(e.target.value)} />
                </div>
                <div>
                  <label style={S.label}>Até</label>
                  <input type="date" style={{ ...S.input, marginBottom: 0 }} value={periodDateTo} onChange={e => setPeriodDateTo(e.target.value)} />
                </div>
                <div>
                  <label style={S.label}>Descrição (opcional)</label>
                  <input type="text" placeholder="ex: Recesso de Natal" style={{ ...S.input, marginBottom: 0 }} value={periodLabel} onChange={e => setPeriodLabel(e.target.value)} />
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" checked={periodAllDay} onChange={e => setPeriodAllDay(e.target.checked)} />
                  Bloquear dia inteiro
                </label>
              </div>
              {!periodAllDay && (
                <div style={{ marginBottom: 8 }}>
                  <label style={S.label}>Horários bloqueados</label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {["06","07","08","09","10","11","12","13","14","15","16","17","18","19","20","21","22"].map(h => {
                      const hInt = parseInt(h);
                      const active = (periodHours || []).includes(hInt);
                      return (
                        <button key={h} style={{ ...S.btn(active ? C.accent : C.muted, !active), padding: "4px 10px", fontSize: 12 }}
                          onClick={() => setPeriodHours(prev => active ? prev.filter(x => x !== hInt) : [...prev, hInt])}>
                          {h}h
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <button style={{ ...S.btn(C.primary), width: "100%", marginTop: 8 }} onClick={addBlockedPeriod}>Confirmar Bloqueio por Período</button>
            </div>
          </div>

          {/* Feriados — full width */}
          <div style={{ ...(isMobile ? S.cardMobile : S.card), gridColumn: isMobile ? "auto" : "1 / -1" }}>
            <div style={S.title}>🗓️ Feriados</div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
              Feriados aplicam as regras de horário de <b>domingo</b> nas quadras.
              Os feriados nacionais fixos (🏛️) são automáticos. Cadastre manualmente feriados móveis
              como Carnaval, Sexta-Feira Santa e Corpus Christi.
            </div>

            {/* Formulário de cadastro */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr auto", gap: 8, marginBottom: 20, alignItems: "flex-end" }}>
              <div>
                <label style={S.label}>Data</label>
                <input
                  type="date"
                  style={{ ...S.input, marginBottom: 0 }}
                  value={holidayDate}
                  onChange={e => setHolidayDate(e.target.value)}
                />
              </div>
              <div>
                <label style={S.label}>Nome do feriado</label>
                <input
                  style={{ ...S.input, marginBottom: 0 }}
                  placeholder="Ex: Carnaval"
                  value={holidayName}
                  onChange={e => setHolidayName(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && addHoliday()}
                />
              </div>
              <button
                style={{ ...S.btn(C.accent), minWidth: 120, alignSelf: "flex-end" }}
                onClick={addHoliday}
              >+ Cadastrar</button>
            </div>

            {/* Lista unificada */}
            {allHolidaysSorted.length === 0 && (
              <div style={{ color: C.muted, fontSize: 13 }}>Nenhum feriado cadastrado além dos nacionais fixos.</div>
            )}
            <div>
              {allHolidaysSorted.map((h, i) => {
                const dateLabel = new Date(h.date + "T12:00:00").toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "long" });
                return (
                  <div key={h.isFixed ? "f-" + h.date : h.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid " + C.border }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{dateLabel}</span>
                        {h.isFixed && (
                          <span style={{ ...S.badge(C.info), fontSize: 10 }}>🏛️ Nacional</span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{h.name}</div>
                    </div>
                    {!h.isFixed && (
                      <button
                        style={{ ...S.btn(C.primary), padding: "6px 12px", fontSize: 12, flexShrink: 0 }}
                        onClick={() => removeHoliday(h.id)}
                      >✕ Remover</button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Inactivity ──────────────────────────────────────────────────────── */}
      {adminTab === "inactivity" && (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>

          {/* Inativos atualmente + form */}
          <div style={isMobile ? S.cardMobile : S.card}>
            <div style={S.title}>😴 Inativos Atualmente</div>
            {currentlyInactive.length === 0 && (
              <div style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>Nenhum jogador inativo no momento.</div>
            )}
            {currentlyInactive.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                {currentlyInactive.map(p => {
                  const histPrev = inactivityHistory.filter(h => h.playerId === p.id);
                  const histDays = histPrev.reduce((acc, h) => acc + h.days, 0);
                  const nowTs = new Date();
                  const currentRun = p.inactiveSince
                    ? Math.max(0, Math.floor((Math.min(nowTs, new Date(p.inactiveUntil)) - new Date(p.inactiveSince + "T12:00:00")) / 86400000))
                    : 0;
                  const ytd = histDays + currentRun;
                  const chip = penaltyChip(ytd);
                  return (
                    <div key={p.id} style={{ padding: "12px 0", borderBottom: "1px solid " + C.border }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Avatar player={p} size={32}/>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{p.position}. {p.name}</div>
                          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                            {p.inactiveSince
                              ? <>Início: {fmt(p.inactiveSince + "T12:00:00")} · Fim: {fmt(p.inactiveUntil)}</>
                              : <>Até: {fmt(p.inactiveUntil)}</>
                            }
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 11, color: C.warning, fontWeight: 600 }}>
                              {ytd} dia{ytd !== 1 ? "s" : ""} acumulados no ano
                            </span>
                            <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 20, background: chip.bg, color: chip.color }}>
                              {chip.label}
                            </span>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                          <button
                            style={{ ...S.btn(C.muted, true), padding: "6px 10px", fontSize: 11 }}
                            onClick={() => { setEditingInactiveId(editingInactiveId === p.id ? null : p.id); setEditInactiveUntil(p.inactiveUntil ? new Date(p.inactiveUntil).toISOString().slice(0,10) : ""); setConfirmEndId(null); }}
                          >✏️</button>
                          <button
                            style={{ ...S.btn(C.primary), padding: "6px 10px", fontSize: 11 }}
                            onClick={() => { setConfirmEndId(confirmEndId === p.id ? null : p.id); setEditingInactiveId(null); }}
                          >Encerrar</button>
                        </div>
                      </div>
                      {confirmEndId === p.id && (
                        <div style={{ marginTop: 10, padding: "10px 12px", background: "#fff8f0", borderRadius: 8, border: "1px solid #e67e2244" }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8 }}>
                            Encerrar inatividade de {p.name}?
                          </div>
                          <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
                            {ytd} dias acumulados serão contabilizados.{ytd > 30 ? " Penalidade sera aplicada." : " Sem penalidade (faixa I)."}
                          </div>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button style={{ ...S.btn(C.primary), flex: 1, fontSize: 12 }} onClick={() => { removeInactivity(p.id); setConfirmEndId(null); }}>Confirmar</button>
                            <button style={{ ...S.btn(C.muted, true), flex: 1, fontSize: 12 }} onClick={() => setConfirmEndId(null)}>Cancelar</button>
                          </div>
                        </div>
                      )}
                      {editingInactiveId === p.id && (
                        <div style={{ marginTop: 10, padding: "10px 12px", background: C.cardAlt, borderRadius: 8, border: "1px solid " + C.border }}>
                          <label style={S.label}>Nova data de fim</label>
                          <input type="date" style={{ ...S.input, marginBottom: 8 }} value={editInactiveUntil} onChange={e => setEditInactiveUntil(e.target.value)}/>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button style={{ ...S.btn(C.accent), flex: 1, fontSize: 12 }} onClick={() => saveInactiveUntil(p)}>Salvar</button>
                            <button style={{ ...S.btn(C.muted, true), flex: 1, fontSize: 12 }} onClick={() => setEditingInactiveId(null)}>Cancelar</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <label style={S.label}>Registrar inatividade</label>
            <div style={{ position: "relative", marginBottom: 10 }}>
              <input
                style={{ ...S.input, marginBottom: 0 }}
                placeholder="Buscar jogador..."
                value={inactiveSearch}
                onChange={e => { setInactiveSearch(e.target.value); setInactiveSearchOpen(true); if (!e.target.value) setInactivePlayer(""); }}
                onFocus={() => setInactiveSearchOpen(true)}
              />
              {inactiveSearchOpen && inactiveSearch.length >= 1 && (() => {
                const matches = sorted.filter(p => p.name.toLowerCase().includes(inactiveSearch.toLowerCase())).slice(0, 8);
                if (!matches.length) return null;
                return (
                  <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: C.card, border: "1px solid " + C.border, borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,.12)", zIndex: 200, maxHeight: 220, overflowY: "auto" }}>
                    {matches.map(p => (
                      <div key={p.id}
                        onMouseDown={() => { setInactivePlayer(String(p.id)); setInactiveSearch(p.name); setInactiveSearchOpen(false); }}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid " + C.border, minHeight: 44 }}>
                        <Avatar player={p} size={28}/>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{p.position}. {p.name}</div>
                          {p.inactiveUntil && new Date(p.inactiveUntil) > new Date() && (
                            <div style={{ fontSize: 11, color: C.warning }}>Inativo ate {fmt(p.inactiveUntil)}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <input style={{ ...S.input, marginBottom: 0, flex: 1 }} type="number" min={15} step={1} value={inactiveDays} onChange={e => setInactiveDays(e.target.value)}/>
              <span style={{ color: C.muted, fontSize: 13, flexShrink: 0 }}>dias (min. 15)</span>
            </div>
            <button style={{ ...S.btn(), width: "100%" }} onClick={setInactivity}>Registrar</button>
          </div>

          {/* Histórico de inatividade */}
          <div style={isMobile ? S.cardMobile : S.card}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
              <div style={S.title}>📋 Histórico de Inatividade</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  style={{ ...S.btn(C.muted, true), fontSize: 12, padding: "6px 14px" }}
                  onClick={loadInactivityHistory}
                  disabled={histLoading}
                >{histLoading ? "Carregando…" : "🔄 Atualizar"}</button>
              </div>
            </div>

            {/* Formulário de entrada manual */}
            <div style={{ background: C.cardAlt, border: "1px solid " + C.border, borderRadius: 10, padding: "14px 16px", marginBottom: 18 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: C.text, marginBottom: 10 }}>➕ Registrar período passado</div>
              <div style={{ position: "relative", marginBottom: 10 }}>
                <input
                  style={{ ...S.input, marginBottom: 0 }}
                  placeholder="Buscar jogador..."
                  value={histPlayerSearch}
                  onChange={e => { setHistPlayerSearch(e.target.value); setHistPlayerSearchOpen(true); if (!e.target.value) setHistPlayerId(""); }}
                  onFocus={() => setHistPlayerSearchOpen(true)}
                />
                {histPlayerSearchOpen && histPlayerSearch.length >= 1 && (() => {
                  const matches = sorted.filter(p => p.name.toLowerCase().includes(histPlayerSearch.toLowerCase())).slice(0, 8);
                  if (!matches.length) return null;
                  return (
                    <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: C.card, border: "1px solid " + C.border, borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,.12)", zIndex: 200, maxHeight: 220, overflowY: "auto" }}>
                      {matches.map(p => (
                        <div key={p.id}
                          onMouseDown={() => { setHistPlayerId(String(p.id)); setHistPlayerSearch(p.name); setHistPlayerSearchOpen(false); }}
                          style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid " + C.border }}>
                          <Avatar player={p} size={24}/>
                          <span style={{ fontSize: 13, fontWeight: 600 }}>{p.position}. {p.name}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr auto", gap: 8, alignItems: "flex-end" }}>
                <div>
                  <label style={S.label}>Início</label>
                  <input type="date" style={{ ...S.input, marginBottom: 0 }} value={histSince} onChange={e => setHistSince(e.target.value)}/>
                </div>
                <div>
                  <label style={S.label}>Fim</label>
                  <input type="date" style={{ ...S.input, marginBottom: 0 }} value={histUntil} onChange={e => setHistUntil(e.target.value)}/>
                </div>
                <div>
                  <label style={S.label}>Penalidade aplicada (posições)</label>
                  <input type="number" min={0} step={5} max={25} style={{ ...S.input, marginBottom: 0 }} value={histPenalty} onChange={e => setHistPenalty(e.target.value)}/>
                </div>
                <button style={{ ...S.btn(C.accent), alignSelf: "flex-end", whiteSpace: "nowrap" }} onClick={addHistoricalInactivity}>+ Adicionar</button>
              </div>
              {histSince && histUntil && histSince < histUntil && (
                <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>
                  {Math.max(1, Math.round((new Date(histUntil) - new Date(histSince)) / 86400000))} dias · período de {new Date(histSince + "T12:00:00").toLocaleDateString("pt-BR")} a {new Date(histUntil + "T12:00:00").toLocaleDateString("pt-BR")}
                </div>
              )}
            </div>

            {/* Lista de histórico */}
            {inactivityHistory.length === 0 && (
              <div style={{ color: C.muted, fontSize: 13 }}>Nenhum histórico registrado. Clique em "Atualizar" para carregar.</div>
            )}
            {inactivityHistory.length > 0 && (() => {
              const byPlayer = {};
              inactivityHistory.forEach(h => {
                if (!byPlayer[h.playerId]) byPlayer[h.playerId] = { name: h.playerName, periods: [] };
                byPlayer[h.playerId].periods.push(h);
              });
              return Object.values(byPlayer).map(({ name, periods }) => {
                const player = sorted.find(p => p.name === name);
                return (
                  <div key={name} style={{ marginBottom: 16, borderBottom: "1px solid " + C.border, paddingBottom: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {player && <Avatar player={player} size={24}/>}
                        <span style={{ fontWeight: 700, fontSize: 13 }}>{player ? `${player.position}. ` : ""}{name}</span>
                      </div>
                      <span style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>
                        {periods.reduce((acc, h) => acc + h.days, 0)} dias acumulados
                      </span>
                    </div>
                    {periods.map(h => {
                      const chip = penaltyChip(h.ytdTotal);
                      const isEditing = editingHistId === h.id;
                      return (
                        <div key={h.id} style={{ borderTop: "1px solid " + C.border }}>
                          {/* linha normal */}
                          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", flexWrap: "wrap" }}>
                            <div style={{ flex: 1, minWidth: 180 }}>
                              <span style={{ fontSize: 12, fontWeight: 600 }}>
                                {new Date(h.since + "T12:00:00").toLocaleDateString("pt-BR")} → {new Date(h.until + "T12:00:00").toLocaleDateString("pt-BR")}
                              </span>
                              <span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>{h.days} dia{h.days !== 1 ? "s" : ""}</span>
                            </div>
                            <span style={{ fontSize: 11, color: h.penalty > 0 ? C.primary : C.success, fontWeight: 600, minWidth: 90, textAlign: "right" }}>
                              Faixa {h.faixa} · {h.penalty > 0 ? `−${h.penalty} posições` : "Sem penalidade"}
                            </span>
                            <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                              <button
                                style={{ ...S.btn(C.muted, true), padding: "4px 8px", fontSize: 11 }}
                                onClick={() => {
                                  if (isEditing) { setEditingHistId(null); return; }
                                  setEditingHistId(h.id);
                                  setEditHistSince(h.since);
                                  setEditHistUntil(h.until);
                                  setEditHistPenalty(h.penalty);
                                }}
                              >{isEditing ? "✕" : "✏️"}</button>
                              <button
                                style={{ ...S.btn(C.primary), padding: "4px 8px", fontSize: 11 }}
                                onClick={() => deleteHistoricalInactivity(h.id)}
                              >🗑️</button>
                            </div>
                          </div>
                          {/* inline edit */}
                          {isEditing && (
                            <div style={{ padding: "10px 12px", marginBottom: 8, background: C.cardAlt, borderRadius: 8, border: "1px solid " + C.border }}>
                              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr auto", gap: 8, alignItems: "flex-end" }}>
                                <div>
                                  <label style={S.label}>Início</label>
                                  <input type="date" style={{ ...S.input, marginBottom: 0 }} value={editHistSince} onChange={e => setEditHistSince(e.target.value)}/>
                                </div>
                                <div>
                                  <label style={S.label}>Fim</label>
                                  <input type="date" style={{ ...S.input, marginBottom: 0 }} value={editHistUntil} onChange={e => setEditHistUntil(e.target.value)}/>
                                </div>
                                <div>
                                  <label style={S.label}>Penalidade (posições)</label>
                                  <input type="number" min={0} step={5} max={25} style={{ ...S.input, marginBottom: 0 }} value={editHistPenalty} onChange={e => setEditHistPenalty(e.target.value)}/>
                                </div>
                                <button style={{ ...S.btn(C.accent), alignSelf: "flex-end" }} onClick={updateHistoricalInactivity}>Salvar</button>
                              </div>
                              {editHistSince && editHistUntil && editHistSince < editHistUntil && (
                                <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>
                                  {Math.max(1, Math.round((new Date(editHistUntil) - new Date(editHistSince)) / 86400000))} dias calculados
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* ── Courts ─────────────────────────────────────────────────────────── */}
      {adminTab === "courts" && (() => {
        const DAY_TYPES = [
          { key: "weekday",  label: "Segunda a Sexta" },
          { key: "saturday", label: "Sábado" },
          { key: "sunday",   label: "Domingo" },
          { key: "holiday",  label: "Feriado" },
        ];
        const courtSlots = state.courtSlots || [];
        const blockedPeriods = state.blockedPeriods || [];
        const fmtPeriodDate = d => d ? new Date(d + "T12:00:00").toLocaleDateString("pt-BR") : "—";
        return (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>

            {/* ── Horários por tipo de dia ── */}
            <div style={{ ...(isMobile ? S.cardMobile : S.card), gridColumn: isMobile ? "auto" : "1 / -1" }}>
              <div style={{ ...S.title }}>🕐 Horários disponíveis por tipo de dia</div>
              {DAY_TYPES.map(({ key, label }) => {
                const slots = courtSlots.filter(s => s.dayType === key).sort((a, b) => a.hour.localeCompare(b.hour));
                return (
                  <div key={key} style={{ marginBottom: 20 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: C.text, marginBottom: 8, borderBottom: "1px solid " + C.border, paddingBottom: 4 }}>{label}</div>
                    {slots.length === 0 && <div style={{ fontSize: 12, color: C.muted }}>Nenhum horário cadastrado.</div>}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                      {slots.map(sl => {
                        const isEditing = editingSlotId === sl.id;
                        return (
                          <div key={sl.id} style={{ display: "flex", alignItems: "center", gap: 4, background: C.cardAlt, border: "1px solid " + C.border, borderRadius: 8, padding: "4px 10px", fontSize: 13 }}>
                            <span style={{ fontWeight: 600 }}>{sl.hour}</span>
                            {isEditing ? (
                              <>
                                <input type="number" min={1} max={4} value={editSlotCourts}
                                  onChange={e => setEditSlotCourts(parseInt(e.target.value) || 1)}
                                  style={{ width: 40, border: "1px solid " + C.border, borderRadius: 4, padding: "2px 4px", fontSize: 12, textAlign: "center" }}
                                />
                                <span style={{ fontSize: 11, color: C.muted }}>qd</span>
                                <button style={{ ...S.btn(C.accent), padding: "2px 8px", fontSize: 11 }}
                                  onClick={() => { updateCourtSlot(sl.id, editSlotCourts); setEditingSlotId(null); }}>✓</button>
                                <button style={{ ...S.btn(C.muted, true), padding: "2px 6px", fontSize: 11 }}
                                  onClick={() => setEditingSlotId(null)}>✕</button>
                              </>
                            ) : (
                              <>
                                <span style={{ fontSize: 11, color: C.muted }}>{sl.maxCourts} qd</span>
                                <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: C.muted, padding: "0 2px" }}
                                  onClick={() => { setEditingSlotId(sl.id); setEditSlotCourts(sl.maxCourts); }}>✏️</button>
                                <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: C.primary, padding: "0 2px" }}
                                  onClick={() => removeCourtSlot(sl.id)}>✕</button>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Adicionar novo horário */}
              <div style={{ borderTop: "1px solid " + C.border, paddingTop: 12, marginTop: 4 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Adicionar horário</div>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "2fr 1fr 1fr auto", gap: 8, alignItems: "flex-end" }}>
                  <div>
                    <label style={S.label}>Tipo de dia</label>
                    <select style={{ ...S.input, marginBottom: 0 }} value={slotDayType} onChange={e => setSlotDayType(e.target.value)}>
                      {DAY_TYPES.map(({ key, label }) => <option key={key} value={key}>{label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={S.label}>Horário</label>
                    <input type="time" style={{ ...S.input, marginBottom: 0 }} value={slotHour} onChange={e => setSlotHour(e.target.value)} />
                  </div>
                  <div>
                    <label style={S.label}>Quadras</label>
                    <input type="number" min={1} max={4} style={{ ...S.input, marginBottom: 0 }} value={slotMaxCourts} onChange={e => setSlotMaxCourts(parseInt(e.target.value) || 1)} />
                  </div>
                  <button style={{ ...S.btn(C.accent), alignSelf: "flex-end" }} onClick={addCourtSlot}>Adicionar</button>
                </div>
              </div>
            </div>

          </div>
        );
      })()}

      {/* ── Rules ──────────────────────────────────────────────────────────── */}
      {adminTab === "rules" && (
        <div>
          {(state.rules || RULES_SECTIONS.map((r,i) => ({...r, id: i+1}))).map(sec => (
            <div key={sec.id} style={{ ...(isMobile ? S.cardMobile : S.card), marginBottom: 8 }}>
              {editingRule?.id === sec.id ? (
                <div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <input style={{ ...S.input, marginBottom: 0, width: 60, flexShrink: 0, textAlign: "center", fontSize: 20 }} value={editRuleIcon} onChange={e => setEditRuleIcon(e.target.value)} placeholder="🔺"/>
                    <input style={{ ...S.input, marginBottom: 0, flex: 1, fontWeight: 700 }} value={editRuleTitle} onChange={e => setEditRuleTitle(e.target.value)} placeholder="Título"/>
                  </div>
                  <textarea style={{ ...S.input, marginBottom: 8, minHeight: 120, resize: "vertical", fontFamily: font, lineHeight: 1.7 }} value={editRuleText} onChange={e => setEditRuleText(e.target.value)}/>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button style={{ ...S.btn(C.accent), flex: 1 }} onClick={saveRule}>Salvar</button>
                    <button style={{ ...S.btn(C.muted, true), flex: 1 }} onClick={() => setEditingRule(null)}>Cancelar</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: fontCond, fontWeight: 800, fontSize: 15, color: C.text, marginBottom: 4 }}>{sec.title}</div>
                    <div style={{ fontSize: 13, color: C.muted, whiteSpace: "pre-line", lineHeight: 1.7 }}>{sec.text}</div>
                  </div>
                  <button
                    style={{ ...S.btn(C.muted, true), padding: "6px 12px", fontSize: 12, flexShrink: 0 }}
                    onClick={() => { setEditingRule(sec); setEditRuleIcon(sec.icon); setEditRuleTitle(sec.title); setEditRuleText(sec.text); }}
                  >✏️</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Result Confirm Modal ──────────────────────────────────────────── */}
      {resultConfirmModal && (() => {
        const ch = state.challenges.find(c => c.id === parseInt(resultChalId));
        const chrName = ch ? state.players.find(p => p.id === ch.challengerId)?.name : "?";
        const chdName = ch ? state.players.find(p => p.id === ch.challengedId)?.name : "?";
        const scoreStr = resultSets.filter(s => s.a !== "" || s.b !== "").map(s => s.a + "/" + s.b).join(", ");
        let summary = "";
        if (resultType === "wo-challenged") summary = `W.O. — ${chdName} não compareceu. ${chrName} vence.`;
        else if (resultType === "wo-challenger") summary = `W.O. — ${chrName} não compareceu. ${chdName} vence.`;
        else if (resultWinner === "challenger") summary = `Vitória de ${chrName}${scoreStr ? " · " + scoreStr : ""}.`;
        else summary = `Vitória de ${chdName}${scoreStr ? " · " + scoreStr : ""}.`;
        return (
          <div style={{ position: "fixed", inset: 0, background: "#0008", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
            <div style={{ background: C.card, borderRadius: 16, padding: 28, maxWidth: 380, width: "100%", boxShadow: "0 8px 40px #0004" }}>
              <div style={{ fontFamily: fontCond, fontWeight: 900, fontSize: 18, color: C.text, marginBottom: 8 }}>⚠️ Confirmar Resultado</div>
              <div style={{ fontSize: 14, color: C.muted, marginBottom: 6 }}>Esta ação é irreversível e afetará posições no ranking.</div>
              <div style={{ background: C.cardAlt, border: "1px solid " + C.border, borderRadius: 10, padding: "12px 16px", marginBottom: 20, fontSize: 14, fontWeight: 600, color: C.text }}>
                {chrName} <span style={{ color: C.muted, fontWeight: 400 }}>vs</span> {chdName}<br/>
                <span style={{ color: C.muted, fontWeight: 400, fontSize: 13 }}>{summary}</span>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button style={{ ...S.btn(C.muted, true), flex: 1 }} onClick={() => setResultConfirmModal(false)}>Cancelar</button>
                <button style={{ ...S.btn(C.success), flex: 1 }} onClick={registerResult}>Confirmar</button>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}
